/**
 * Browser fingerprint utility
 * Generates a stable SHA-256 hash from browser characteristics
 * Used to bind refresh tokens to a specific browser instance
 */
export class FingerprintUtil {
    private static cachedFingerprint: string | null = null;
    private static readonly LS_KEY = 'browser_fingerprint_hash';

    /**
     * Get the browser fingerprint hash (cached)
     * Returns from memory cache > localStorage > generates fresh
     */
    static async getFingerprint(): Promise<string> {
        // Memory cache
        if (this.cachedFingerprint) {
            return this.cachedFingerprint;
        }

        // localStorage cache
        const stored = localStorage.getItem(this.LS_KEY);
        if (stored) {
            this.cachedFingerprint = stored;
            return stored;
        }

        // Generate fresh
        const fingerprint = await this.generateFingerprint();
        this.cachedFingerprint = fingerprint;
        localStorage.setItem(this.LS_KEY, fingerprint);
        return fingerprint;
    }

    /**
     * Clear cached fingerprint
     */
    static clearCache(): void {
        this.cachedFingerprint = null;
        localStorage.removeItem(this.LS_KEY);
    }

    /**
     * Generate a fingerprint hash from browser characteristics
     */
    private static async generateFingerprint(): Promise<string> {
        const components: string[] = [];

        // Navigator properties
        components.push(navigator.userAgent || '');
        components.push(navigator.language || '');
        components.push(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
        components.push(navigator.platform || '');
        components.push(String(navigator.hardwareConcurrency || 0));

        // Canvas fingerprint
        components.push(this.getCanvasFingerprint());

        // WebGL renderer
        components.push(this.getWebGLRenderer());

        const raw = components.join('|');
        return this.sha256(raw);
    }

    /**
     * Canvas rendering fingerprint
     */
    private static getCanvasFingerprint(): string {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 200;
            canvas.height = 50;
            const ctx = canvas.getContext('2d');
            if (!ctx) return '';

            // Draw text with specific font
            ctx.textBaseline = 'top';
            ctx.font = '14px Arial';
            ctx.fillStyle = '#f60';
            ctx.fillRect(125, 1, 62, 20);
            ctx.fillStyle = '#069';
            ctx.fillText('BrowserFP', 2, 15);
            ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
            ctx.fillText('BrowserFP', 4, 17);

            // Draw shapes
            ctx.beginPath();
            ctx.arc(50, 25, 10, 0, Math.PI * 2);
            ctx.fill();

            return canvas.toDataURL();
        } catch {
            return '';
        }
    }

    /**
     * WebGL renderer string
     */
    private static getWebGLRenderer(): string {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (!gl) return '';

            const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
            if (!debugInfo) return '';

            return (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
        } catch {
            return '';
        }
    }

    /**
     * SHA-256 hash using Web Crypto API
     */
    private static async sha256(input: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(input);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
}
